import Status from "@/modules/common/components/ui-kit/status/status";
import Label from "@/modules/common/components/ui-kit/label";
import ActivityDetailsText from "@/modules/lead-page/new/components/activity/activity-details-typography";
import UserNameTag from "@/modules/lead-page/new/components/activity/user-name-tag";
import { Stack, Typography, useTheme } from "@mui/material";
import { useMemo } from "react";

interface StatusUpdateDetailsProps {
  actor: {
    name: string | null;
    color: string | null;
  };
  previousStatus?: {
    name: string;
    color: string | null;
  };
  newStatus: {
    name: string;
    color: string | null;
  };
  triggeredBy?: string | null;
}
const StatusUpdateDetails = ({
  actor,
  previousStatus,
  newStatus,
  triggeredBy,
}: StatusUpdateDetailsProps) => {
  const theme = useTheme();

  const hasPreviousStatus = Boolean(previousStatus);

  const hasActorName = Boolean(actor.name);

  const changeText = useMemo(() => {
    if (hasPreviousStatus) {
      return hasActorName ? "changed the status from" : "Status changed from";
    }
    return hasActorName ? "set the status to" : "Status set to";
  }, [hasPreviousStatus, hasActorName]);

  const showTriggeredBy =
    triggeredBy &&
    ["crm", "zoom_call", "phone_call"].indexOf(triggeredBy.toLowerCase()) === -1;

  return (
    <Stack direction="column" gap={theme.nawy.spacing.xs}>
      <Stack
        direction="row"
        spacing={theme.nawy.spacing.xxs}
        flexWrap="wrap"
        alignItems="center"
        rowGap={theme.nawy.spacing.xxs}
      >
        {actor.name && <UserNameTag name={actor.name} color={actor.color} />}

        <ActivityDetailsText>{changeText}</ActivityDetailsText>
        {hasPreviousStatus && previousStatus ? (
          <>
            <Status
              label={previousStatus.name}
              bgHexColor={previousStatus.color || ""}
              size="md"
              width="auto"
            />

            <ActivityDetailsText>to</ActivityDetailsText>
          </>
        ) : null}

        <Status
          label={newStatus.name}
          bgHexColor={newStatus.color || ""}
          size="md"
          width="auto"
        />
      </Stack>

      {showTriggeredBy && (
        <Stack
          direction="row"
          spacing={theme.nawy.spacing.xxs}
          alignItems="center"
        >
          <Typography
            sx={{
              color: theme.nawy.colors.semantic.text.primary,
              ...theme.nawy.typography.english.variants.body.sm.semibold,
            }}
          >
            Triggered By:
          </Typography>
          <Label label={triggeredBy} variant="secondary" color="navy" />
        </Stack>
      )}
    </Stack>
  );
};

export default StatusUpdateDetails;
